import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import usersService from '../../../../services/users.service';
import { useEyeconxStore } from '../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../utils/ecxSwal';
import { ClinicInfoPanel } from '../clinicsettings/info/clinicInfoParts';
import ClinicSettingsEmpty from '../clinicsettings/subparts/ClinicSettingsEmpty';
import ClinicSettingsFormFooter from '../clinicsettings/subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../clinicsettings/subparts/ClinicSettingsListSkeleton';
import ClinicSettingsSectionHeader from '../clinicsettings/subparts/ClinicSettingsSectionHeader';

const ALL_ROLES = 'ALL';

const ROLE_ORDER = ['ADMIN', 'MANAGER', 'ECP', 'STAFF', 'OTHER'];

const ROLE_META = {
    ADMIN: { label: 'Administrators', icon: 'bi-shield-lock' },
    MANAGER: { label: 'Managers', icon: 'bi-person-badge' },
    ECP: { label: 'Eye Care Professionals', icon: 'bi-eye' },
    STAFF: { label: 'Staff Members', icon: 'bi-people' },
    OTHER: { label: 'Other Roles', icon: 'bi-person' },
};

const getInitials = (user) => {
    const first = user?.firstName?.charAt(0) || '';
    const last = user?.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
};

const getRoleDisplayName = (role) => ROLE_META[role]?.label || `${role}s`;

const getRoleIcon = (role) => ROLE_META[role]?.icon || 'bi-person';

const getUserDisplayName = (user) =>
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.username || 'User';

const UserOverviewStats = ({ total, active, authenticated }) => (
    <div className="user-overview-stats">
        <div className="user-overview-stat">
            <span className="user-overview-stat__icon">
                <i className="bi bi-people"></i>
            </span>
            <div>
                <span className="user-overview-stat__label">Total Users</span>
                <span className="user-overview-stat__value">{total}</span>
            </div>
        </div>
        <div className="user-overview-stat">
            <span className="user-overview-stat__icon is-enabled">
                <i className="bi bi-check-circle-fill"></i>
            </span>
            <div>
                <span className="user-overview-stat__label">Active</span>
                <span className="user-overview-stat__value">{active}</span>
            </div>
        </div>
        <div className="user-overview-stat">
            <span className="user-overview-stat__icon">
                <i className="bi bi-shield-check"></i>
            </span>
            <div>
                <span className="user-overview-stat__label">Authenticated</span>
                <span className="user-overview-stat__value">{authenticated}</span>
            </div>
        </div>
    </div>
);

const UserRoleFilter = ({ selectedRole, availableRoles, isEditing, isSaving, onRoleChange }) => (
    <div className="user-role-filter">
        <div className="user-role-filter__field">
            <label className="user-role-filter__label" htmlFor="user-role-filter-select">
                Filter By Role
            </label>
            <select
                id="user-role-filter-select"
                className="form-select form-select-sm user-role-filter__select"
                value={selectedRole}
                onChange={(event) => onRoleChange(event.target.value)}
                disabled={isEditing || isSaving}
            >
                <option value={ALL_ROLES}>All Roles</option>
                {availableRoles.map((role) => (
                    <option key={role} value={role}>
                        {getRoleDisplayName(role)}
                    </option>
                ))}
            </select>
        </div>
    </div>
);

const UserStatusPills = ({ user }) => (
    <div className="user-list-item__status-group">
        {user.isBlocked && (
            <span className="clinic-settings-status-pill is-closed">
                <i className="bi bi-slash-circle"></i>
                Blocked
            </span>
        )}
        <span className={`clinic-settings-status-pill ${user.isActive ? 'is-open' : 'is-closed'}`}>
            <i className={`bi ${user.isActive ? 'bi-check-circle-fill' : 'bi-pause-circle-fill'}`}></i>
            {user.isActive ? 'Active' : 'Inactive'}
        </span>
        <span className={`clinic-settings-status-pill ${user.isAuthenticated ? 'is-active' : 'is-closed'}`}>
            <i className={`bi ${user.isAuthenticated ? 'bi-shield-check' : 'bi-clock'}`}></i>
            {user.isAuthenticated ? 'Authenticated' : 'Pending'}
        </span>
    </div>
);

const UserEditForm = ({ user, onSave, onCancel, isSaving }) => {
    const { register, handleSubmit, formState: { errors } } = useForm({
        defaultValues: user,
    });

    return (
        <form onSubmit={handleSubmit(onSave)} className="user-edit-form">
            <div className="user-edit-form__grid">
                <div>
                    <label className="form-label small fw-normal text-muted">First Name</label>
                    <input
                        type="text"
                        className={`form-control form-control-sm ${errors.firstName ? 'is-invalid' : ''}`}
                        {...register('firstName', { required: 'First name is required' })}
                    />
                    {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
                </div>
                <div>
                    <label className="form-label small fw-normal text-muted">Last Name</label>
                    <input
                        type="text"
                        className={`form-control form-control-sm ${errors.lastName ? 'is-invalid' : ''}`}
                        {...register('lastName', { required: 'Last name is required' })}
                    />
                    {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
                </div>
                <div>
                    <label className="form-label small fw-normal text-muted">Username</label>
                    <div className="form-control form-control-sm bg-light text-muted">{user.username || '—'}</div>
                    <div className="form-text">Username can only be changed by the user from their profile.</div>
                </div>
                <div>
                    <label className="form-label small fw-normal text-muted">Email</label>
                    <input
                        type="email"
                        className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                        {...register('email', {
                            required: 'Email is required',
                            pattern: {
                                value: /^\S+@\S+$/i,
                                message: 'Invalid email address',
                            },
                        })}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                </div>
            </div>

            <div className="user-edit-form__switches">
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" disabled {...register('isActive')} />
                    <label className="form-check-label small">Active</label>
                </div>
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" disabled {...register('isBlocked')} />
                    <label className="form-check-label small">Blocked</label>
                </div>
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" disabled {...register('isAuthenticated')} />
                    <label className="form-check-label small">Authenticated</label>
                </div>
            </div>

            <div className="user-edit-form__footer">
                <ClinicSettingsFormFooter
                    isSaving={isSaving}
                    submitLabel="Save Changes"
                    showCancel
                    onCancel={onCancel}
                />
            </div>
        </form>
    );
};

const UserListItem = ({ user, onEdit, onReminders, isEditing, onSave, onCancelEdit, isSaving }) => (
    <div
        className={`clinic-settings-record-item user-list-item ${
            isEditing ? 'clinic-settings-record-item--editing' : ''
        } ${user.isActive ? 'user-list-item--active' : 'user-list-item--inactive'}`}
    >
        {isEditing ? (
            <div className="flex-grow-1">
                <div className="user-list-item__edit-header mb-3">
                    <span className="clinic-settings-record-item__avatar">{getInitials(user)}</span>
                    <div>
                        <h6 className="clinic-settings-record-item__title mb-0">{getUserDisplayName(user)}</h6>
                        <p className="clinic-settings-record-item__meta mb-0">Editing account details</p>
                    </div>
                </div>
                <UserEditForm user={user} onSave={onSave} onCancel={onCancelEdit} isSaving={isSaving} />
            </div>
        ) : (
            <>
                <span className="clinic-settings-record-item__avatar">{getInitials(user)}</span>
                <div className="clinic-settings-record-item__main">
                    <h6 className="clinic-settings-record-item__title">{getUserDisplayName(user)}</h6>
                    <p className="clinic-settings-record-item__meta mb-1">
                        <i className="bi bi-envelope me-1"></i>
                        {user.email || 'No email provided'}
                    </p>
                    <p className="clinic-settings-record-item__meta mb-0">
                        <span className="me-3">
                            <i className="bi bi-at me-1"></i>
                            {user.username}
                        </span>
                        {user.number && (
                            <span>
                                <i className="bi bi-hash me-1"></i>
                                {user.number}
                            </span>
                        )}
                    </p>
                </div>
                <UserStatusPills user={user} />
                <div className="clinic-settings-record-item__actions">
                    {user.role === 'ECP' && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary rounded-pill"
                            onClick={() => onReminders(user)}
                        >
                            <i className="bi bi-bell me-1"></i>
                            Reminders
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-light-primary rounded-pill"
                        onClick={() => onEdit(user)}
                    >
                        <i className="bi bi-pencil me-1"></i>
                        Edit
                    </button>
                </div>
            </>
        )}
    </div>
);

const UserRolePanel = ({ role, roleUsers, editingId, onEdit, onReminders, onSave, onCancelEdit, isSaving }) => (
    <ClinicInfoPanel
        title={getRoleDisplayName(role)}
        icon={getRoleIcon(role)}
        actions={
            <span className="ecx-page-chip">
                <i className="bi bi-people"></i>
                {roleUsers.length}
            </span>
        }
    >
        <div className="clinic-settings-record-list">
            {roleUsers.map((user) => (
                <UserListItem
                    key={user.id}
                    user={user}
                    isEditing={editingId === user.id}
                    onEdit={onEdit}
                    onReminders={onReminders}
                    onSave={onSave}
                    onCancelEdit={onCancelEdit}
                    isSaving={isSaving}
                />
            ))}
        </div>
    </ClinicInfoPanel>
);

const UserCard = () => {
    const navigate = useNavigate();
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [users, setUsers] = useState([]);
    const [groupedUsers, setGroupedUsers] = useState({});
    const [selectedRole, setSelectedRole] = useState(ALL_ROLES);
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const fetchUsers = useCallback(async () => {
        if (!clinicId) {
            return;
        }

        setIsLoading(true);
        setLoadFailed(false);

        try {
            const response = await usersService.getUsers(clinicId);
            const userList = response.data || [];
            setUsers(userList);

            const grouped = userList.reduce((acc, user) => {
                const role = user.role || 'OTHER';
                if (!acc[role]) {
                    acc[role] = [];
                }
                acc[role].push(user);
                return acc;
            }, {});

            const sorted = {};
            ROLE_ORDER.forEach((role) => {
                if (grouped[role]) {
                    sorted[role] = grouped[role];
                }
            });
            Object.keys(grouped).forEach((role) => {
                if (!sorted[role]) {
                    sorted[role] = grouped[role];
                }
            });

            setGroupedUsers(sorted);
        } catch (error) {
            console.error(error);
            setLoadFailed(true);
            showError({
                title: 'Load Failed',
                text: 'Unable to load clinic users. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const stats = useMemo(() => ({
        total: users.length,
        active: users.filter((user) => user.isActive).length,
        authenticated: users.filter((user) => user.isAuthenticated).length,
    }), [users]);

    const availableRoles = useMemo(
        () => ROLE_ORDER.filter((role) => groupedUsers[role]?.length),
        [groupedUsers]
    );

    const visibleGroups = useMemo(() => {
        if (selectedRole === ALL_ROLES) {
            return groupedUsers;
        }

        return groupedUsers[selectedRole] ? { [selectedRole]: groupedUsers[selectedRole] } : {};
    }, [groupedUsers, selectedRole]);

    const visibleUserCount = useMemo(
        () => Object.values(visibleGroups).reduce((count, roleUsers) => count + roleUsers.length, 0),
        [visibleGroups]
    );

    const handleEdit = (user) => {
        setEditingId(user.id);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
    };

    const handleRoleChange = (nextRole) => {
        if (editingId) {
            setEditingId(null);
        }
        setSelectedRole(nextRole);
    };

    const onSubmit = async (formData) => {
        setIsSaving(true);

        try {
            const payload = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
            };

            await usersService.updateUser(clinicId, editingId, payload);
            setEditingId(null);
            await fetchUsers();

            await showSuccess({
                title: 'User Updated',
                text: 'User details were saved successfully.',
            });
        } catch (error) {
            console.error(error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update user details. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleUserReminders = (user) => {
        navigate(`/settings/reminders?ecp=${user.id}`);
    };

    const roleFilter = (
        <UserRoleFilter
            selectedRole={selectedRole}
            availableRoles={availableRoles}
            isEditing={Boolean(editingId)}
            isSaving={isSaving}
            onRoleChange={handleRoleChange}
        />
    );

    if (isLoading) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="User Overview"
                    icon="bi-people"
                    subtitle="Manage clinic users, roles, and account details"
                />
                {roleFilter}
                <UserOverviewStats total={0} active={0} authenticated={0} />
                <ClinicInfoPanel title="Users" icon="bi-people">
                    <ClinicSettingsListSkeleton rows={6} />
                </ClinicInfoPanel>
            </>
        );
    }

    if (loadFailed) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="User Overview"
                    icon="bi-people"
                    subtitle="Manage clinic users, roles, and account details"
                />
                {roleFilter}
                <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3">
                    <span>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        Failed to load clinic users.
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-danger rounded-pill" onClick={fetchUsers}>
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Retry
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <ClinicSettingsSectionHeader
                title="User Overview"
                icon="bi-people"
                subtitle="Manage clinic users, roles, and account details"
                actions={
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="ecx-page-chip">
                            <i className="bi bi-people"></i>
                            {visibleUserCount} user{visibleUserCount === 1 ? '' : 's'}
                        </span>
                        <span className="ecx-page-chip">
                            <i className="bi bi-check-circle"></i>
                            {stats.active} active
                        </span>
                    </div>
                }
            />

            {roleFilter}

            <UserOverviewStats
                total={stats.total}
                active={stats.active}
                authenticated={stats.authenticated}
            />

            {users.length === 0 ? (
                <ClinicSettingsEmpty
                    icon="bi-people"
                    title="No users found"
                    description="Clinic users will appear here once they are added."
                />
            ) : visibleUserCount === 0 ? (
                <ClinicSettingsEmpty
                    icon="bi-funnel"
                    title="No users in this role"
                    description="Try selecting a different role filter."
                />
            ) : (
                <div className="user-role-panels">
                    {Object.entries(visibleGroups).map(([role, roleUsers]) => (
                        <UserRolePanel
                            key={role}
                            role={role}
                            roleUsers={roleUsers}
                            editingId={editingId}
                            onEdit={handleEdit}
                            onReminders={handleUserReminders}
                            onSave={onSubmit}
                            onCancelEdit={handleCancelEdit}
                            isSaving={isSaving}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export default UserCard;
